'use client';
import type { MenuItem } from '@hen-site/lib/wordpressApiClient';
import type { FooterData } from '@hen-site/types/layout';
import { FooterSiteMessage } from '@hen-site/component/FooterSiteMessage';
import flatListToHierarchical from '@hen-site/utils/flatListToHierarchical';
import { asString } from '@hen-site/utils/safeString';

import {
  Footer as FooterComponent,
  Image,
  Link,
  Text,
} from '@highereducation/hen-ui';

interface FooterProps {
  footerData?: FooterData;
}

const ONE_TRUST_TRIGGER_CLASS = 'ot-sdk-show-settings';
const ONE_TRUST_FOOTER_MODIFIER = 'ot-sdk-show-settings--footer';

/**
 * Legacy label used to detect the OneTrust cookie-preferences trigger before
 * we moved the binding to WP `cssClasses`. Kept as a transitional fallback so
 * tenants whose WP menu items haven't been updated yet still get a working
 * OneTrust modal. Safe to remove once every tenant has set the trigger class
 * in WP — no urgent follow-up ticket, just an opportunistic cleanup.
 */
const LEGACY_ONE_TRUST_LABEL = 'Cookie Settings';

/**
 * Renders a single footer menu link.
 *
 * Any CSS classes set on the WordPress menu item (via Appearance → Menus →
 * Screen Options → CSS Classes) are forwarded onto the rendered anchor. This
 * lets content editors mark a menu item as the OneTrust cookie-preferences
 * trigger by adding the `ot-sdk-show-settings` class to it — anywhere in the
 * footer hierarchy — without engineering changes. When the trigger is detected
 * we also (a) add the `ot-sdk-show-settings--footer` BEM modifier for styling
 * parity with the legacy behavior and (b) drop `target="_blank"` because
 * OneTrust opens a modal in-page.
 *
 * Non-trigger links open in a new tab. They always carry
 * `rel="noopener noreferrer"` to prevent reverse-tabnabbing (a `window.opener`
 * navigation back to the parent site). The OneTrust trigger keeps no `rel`
 * because it doesn't navigate at all — it opens an in-page modal.
 *
 * Until every tenant has the class set on the right menu item, we fall back
 * to the legacy label match. The label fallback is scheduled for removal.
 */
function renderFooterLink(link: MenuItem) {
  const cssClasses = link.cssClasses ?? [];
  const isOneTrustTrigger =
    cssClasses.includes(ONE_TRUST_TRIGGER_CLASS) ||
    link.label === LEGACY_ONE_TRUST_LABEL;

  const renderedClasses = ['font-normal hover:text-white'];
  if (isOneTrustTrigger) {
    if (!cssClasses.includes(ONE_TRUST_TRIGGER_CLASS)) {
      renderedClasses.push(ONE_TRUST_TRIGGER_CLASS);
    }
    if (!cssClasses.includes(ONE_TRUST_FOOTER_MODIFIER)) {
      renderedClasses.push(ONE_TRUST_FOOTER_MODIFIER);
    }
  }
  renderedClasses.push(...cssClasses);

  return (
    <Link
      key={link.id}
      variant='white'
      href={link.uri}
      target={isOneTrustTrigger ? undefined : '_blank'}
      rel={isOneTrustTrigger ? undefined : 'noopener noreferrer'}
      size='sm'
      className={renderedClasses.filter(Boolean).join(' ')}
    >
      {link.label}
    </Link>
  );
}

export function Footer({ footerData }: FooterProps) {
  if (!footerData || !footerData.menu || !footerData.auxiliaryMenu) {
    return null;
  }

  const finalFooterMenuList: MenuItem[] = flatListToHierarchical(
    footerData.menu,
  );

  const currentYear = new Date().getFullYear();
  const footerLogoSrc =
    asString(footerData.logoWhite) || asString(footerData.logo);

  return (
    <FooterComponent>
      <FooterComponent.Top>
        <FooterComponent.Brand
          logo={
            <Image
              src={footerLogoSrc}
              alt='Logo'
              objectFit='contain'
              className='h-10 w-auto self-start'
            />
          }
        >
          <FooterSiteMessage html={footerData.siteFooterMessage} />
        </FooterComponent.Brand>
        <FooterComponent.SectionGroup>
          {finalFooterMenuList.map((section) => (
            <FooterComponent.Section key={section.id} title={section.label}>
              {section.children.map((link) => renderFooterLink(link))}
            </FooterComponent.Section>
          ))}
        </FooterComponent.SectionGroup>
      </FooterComponent.Top>
      <FooterComponent.Bottom>
        <Text variant='b5' color='white'>
          © {currentYear} {footerData.name}, a Red Ventures Company
        </Text>
        <FooterComponent.Links>
          {footerData.auxiliaryMenu.map((link) => renderFooterLink(link))}
        </FooterComponent.Links>
      </FooterComponent.Bottom>
    </FooterComponent>
  );
}
